import { LoadingOverlay } from '@eventbrite/eds-loading-overlay';
import classNames from 'classnames';
import React, { FunctionComponent, useState } from 'react';
import { Locale } from '../../api/models/locale';
import { getLoadingOverlayStyle } from '../../utils/rebrand';
import LanguageSelector from '../languageSelector';
import PoweredByEventbriteText from '../poweredByEventbriteText';

export interface LanguageAndBrandProps {
    defaultLocale: string;
    isAppRebrandActive?: boolean;
    shouldShowLanguageLabel?: boolean;
    isNativeApp: boolean;
    isSeriesPage?: boolean;
    isVertical?: boolean;
    locales: Array<Locale>;
    onSelectLanguage: (newLocale: string, index: number) => void;
}

const LanguageAndBrand: FunctionComponent<LanguageAndBrandProps> = ({
    defaultLocale,
    isSeriesPage,
    isVertical = false,
    isAppRebrandActive = false,
    shouldShowLanguageLabel = true,
    isNativeApp,
    locales,
    onSelectLanguage,
}) => {
    const [isLoading, setIsLoading] = useState(false);

    if (isNativeApp && isAppRebrandActive) {
        return null;
    }

    const handleOnSelectLanguage = (locale: string, index: number) => {
        setIsLoading(true);
        onSelectLanguage(locale, index);
    };

    const languageSelectorClasses = classNames(
        'eds-g-cell',
        'eds-text-color--grey-600',
        {
            'eds-g-cell-6-12': shouldShowLanguageLabel,
            'eds-g-cell-sm-4-12': shouldShowLanguageLabel,
            'eds-text--right': !isVertical && !isAppRebrandActive,
            'eds-g-cell-12-12 g-vert-full-height__language-selector':
                isVertical,
        },
    );

    const brandClasses = classNames('eds-g-cell', {
        'eds-g-cell-6-12 eds-g-cell-sm-8-12 eds-l-pad-bot-2': !isVertical,
        'eds-g-cell-12-12 eds-align--center g-vert-full-height__powered-by-eventbrite':
            isVertical,
    });

    const containerClassName = classNames({
        'eds-l-mar-vert-6': !isVertical && !isSeriesPage,
        'eds-align--center-vertical eds-align--space-between': !isVertical,
        'g-vert-full-height eds-l-mar-top-6 eds-l-mar-bot-10': isVertical,
    });

    const poweredByEventbriteText = !isAppRebrandActive ? (
        <div className={brandClasses} data-spec="brand-text">
            <PoweredByEventbriteText />
        </div>
    ) : null;

    return (
        <div
            className={containerClassName}
            data-spec="language-brand-container"
        >
            {poweredByEventbriteText}
            <div
                className={languageSelectorClasses}
                data-automation="language-selector"
                data-spec="language-selector"
            >
                <div className="u-inline-flex">
                    <LanguageSelector
                        locales={locales}
                        defaultLocale={defaultLocale}
                        isAppRebrandActive={isAppRebrandActive}
                        shouldShowLanguageLabel={shouldShowLanguageLabel}
                        onChange={handleOnSelectLanguage}
                    />
                </div>
            </div>
            <LoadingOverlay
                size="large-chunky"
                isShown={isLoading}
                style={getLoadingOverlayStyle(isAppRebrandActive)}
            />
        </div>
    );
};

export default LanguageAndBrand;
